import {
    Reviewing,
    DecisionDataStore,
    type ApplicationState,
    type SecondLookState,
} from '@servicetitan/financing-decision-shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    SubmitApplicationDecision,
    PrequalificationOutcome,
    type GetLoanApiResponse,
} from '@servicetitan/financing-api-client';
import { SelectedPlanStore, FinancedAmountStore } from '@servicetitan/financing-plans-shared';
import {
    WorkflowStore,
    WorkflowButtonsStore,
    useSecondLookIntegration,
    SetupDataStore,
} from '@servicetitan/financing-workflow-shared';
import {
    ServerErrorState,
    type ServerError,
    openWebUrl,
    useBreakpoint,
} from '@servicetitan/financing-utils-shared';
import {
    PrequalApprovedScreen,
    PrequalDeclinedScreen,
    OpenApplicationConfirmationDialog,
    ExternalApplicationStore,
    FullApplicationFormDialog,
} from '@servicetitan/financing-application-shared';
import { useWellsFargoPrequalSubmission } from './hooks/useWellsFargoPrequalSubmission';
import { useSecondLookSubmission } from './hooks/useSecondLookSubmission';
import { useLoanStatusPolling } from './hooks/useLoanStatusPolling';
import { Dialog, Flex } from '@servicetitan/anvil2';

const WellsFargoReviewingWorkflowInner = () => {
    const { decisionData, setDecisionData } = DecisionDataStore.useStore();
    const [applicationState, setApplicationState] = useState<ApplicationState>('sending');
    const [secondLookState, setSecondLookState] = useState<SecondLookState>('none');
    const [unexpectedOutcome, setUnexpectedOutcome] = useState<string | null>(null);
    const [prequalError, setPrequalError] = useState<ServerError | null>(null);
    const [hasApplied, setHasApplied] = useState(false);
    const { setWorkflowStage } = WorkflowStore.useStore();
    const secondLookIntegration = useSecondLookIntegration();
    const { setSelectedPlan } = SelectedPlanStore.useStore();
    const { financedAmount } = FinancedAmountStore.useStore();
    const { externalApplicationData } = ExternalApplicationStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const { onCloseWorkflow } = WorkflowButtonsStore.useStore();
    const screenSize = useBreakpoint();
    const isNarrowScreen = screenSize.isXs || screenSize.isSm;
    const accessLink = externalApplicationData?.accessLink;

    // Throw during render so ErrorBoundary can catch it
    if (unexpectedOutcome) {
        throw new Error(`Unexpected prequalificationOutcome: ${unexpectedOutcome}`);
    }

    useWellsFargoPrequalSubmission({
        autoSubmit: true,
        onMutate: () => {
            setApplicationState('reviewing');
        },
        onSuccess: response => {
            if (response.prequalificationOutcome === PrequalificationOutcome.Approved) {
                setApplicationState('approved');
            } else if (response.prequalificationOutcome === PrequalificationOutcome.Declined) {
                setDecisionData({
                    ...decisionData,
                    loanId: response.id,
                    decision: SubmitApplicationDecision.Declined,
                });
                setApplicationState('declined');
            } else {
                setUnexpectedOutcome(String(response.prequalificationOutcome));
            }
        },
        onError: error => {
            setPrequalError(error);
        },
    });

    const {
        submitSecondLook,
        loanId: secondLookLoanId,
        hasSubmittedApplication: secondLookHasSubmitted,
        isBelowWaterfallThreshold,
        tenantSettingsLoaded,
    } = useSecondLookSubmission({
        autoSubmit: false,
        onSuccess: () => {
            setSecondLookState('reviewing');
        },
    });

    const decisionDataRef = useRef(decisionData);
    decisionDataRef.current = decisionData;

    const handleSecondLookDecisionChange = useCallback(
        (decision: SubmitApplicationDecision, loanData: GetLoanApiResponse) => {
            if (loanData.redirectUrl) {
                setSelectedPlan(null);
                setDecisionData({
                    ...decisionDataRef.current!,
                    decision: SubmitApplicationDecision.Submitted,
                    loanId: secondLookLoanId,
                    approvedAmount:
                        loanData.requestedAmount != null && loanData.approvedAmount != null
                            ? Math.min(loanData.requestedAmount, loanData.approvedAmount)
                            : (loanData.requestedAmount ?? loanData.approvedAmount ?? null),
                    maxCreditAmount: loanData.approvedAmount ?? null,
                    externalLoanId: loanData.externalId ?? '',
                    redirectUrl: loanData.redirectUrl,
                    declineRedirectUrl: loanData.declineRedirectUrl ?? null,
                });
                setWorkflowStage('second-look-decision');
            } else if (decision === SubmitApplicationDecision.Approved) {
                setSelectedPlan(null);
                setDecisionData({
                    ...decisionDataRef.current!,
                    decision: SubmitApplicationDecision.Approved,
                    loanId: secondLookLoanId,
                    approvedAmount:
                        loanData.requestedAmount != null && loanData.approvedAmount != null
                            ? Math.min(loanData.requestedAmount, loanData.approvedAmount)
                            : (loanData.requestedAmount ?? loanData.approvedAmount ?? null),
                    maxCreditAmount: loanData.approvedAmount ?? null,
                    externalLoanId: loanData.externalId ?? '',
                });
                setWorkflowStage('second-look-decision');
            } else if (decision === SubmitApplicationDecision.Declined) {
                setSecondLookState('declined');
            }
        },
        [secondLookLoanId, setSelectedPlan, setDecisionData, setWorkflowStage],
    );

    const handleSecondLookTimeout = useCallback(() => {
        setSecondLookState('declined');
    }, []);

    useLoanStatusPolling({
        loanId: secondLookLoanId,
        onDecisionChange: handleSecondLookDecisionChange,
        onTimeout: handleSecondLookTimeout,
    });

    useEffect(() => {
        if (
            applicationState === 'declined' &&
            secondLookIntegration.isEnabled &&
            !secondLookHasSubmitted &&
            tenantSettingsLoaded &&
            !isBelowWaterfallThreshold
        ) {
            setSecondLookState('sending');
            submitSecondLook();
        }
    }, [
        applicationState,
        secondLookIntegration.isEnabled,
        submitSecondLook,
        secondLookHasSubmitted,
        tenantSettingsLoaded,
        isBelowWaterfallThreshold,
    ]);

    const handleRetry = useCallback(() => {
        setWorkflowStage('application-with-co-application');
    }, [setWorkflowStage]);

    const [showFullAppForm, setShowFullAppForm] = useState(false);

    const handleApprovedApplyNow = useCallback(() => {
        if (accessLink) {
            openWebUrl(accessLink, setupData?.useInAppBrowser);
            setHasApplied(true);
        }
    }, [accessLink, setupData?.useInAppBrowser]);

    const handleDeclinedApplyNow = useCallback(() => {
        // We need to show a new full application dialog. Although we already have all
        // the necessary personal information, the user still needs to acknowledge the
        // full application disclosure.
        setShowFullAppForm(true);
    }, []);

    const getDecisionComponent = () => {
        if (applicationState === 'approved') {
            return (
                <PrequalApprovedScreen
                    approvedAmount={financedAmount ?? 0}
                    onApplyNow={handleApprovedApplyNow}
                />
            );
        }

        if (applicationState === 'declined') {
            const isTerminalDecline =
                !secondLookIntegration.isEnabled ||
                secondLookState === 'declined' ||
                isBelowWaterfallThreshold;

            if (isTerminalDecline) {
                return <PrequalDeclinedScreen onApplyNow={handleDeclinedApplyNow} />;
            }
        }

        return <Reviewing applicationState={applicationState} secondLookState={secondLookState} />;
    };

    if (prequalError) {
        return (
            <ServerErrorState
                errorDetails={{
                    error: prequalError,
                    actionCausingError: 'submitting your pre-qualification request',
                }}
                onRetry={handleRetry}
            />
        );
    }

    return (
        <>
            <Flex
                direction="column"
                gap="6"
                alignItems="center"
                style={{ maxWidth: '845px', width: '100%', margin: '0 auto' }}
            >
                <div style={{ flexGrow: 1, width: '100%' }}>{getDecisionComponent()}</div>
            </Flex>
            <FullApplicationFormDialog
                open={showFullAppForm}
                onClose={() => setShowFullAppForm(false)}
                onApplicationOpened={() => {
                    setShowFullAppForm(false);
                    setHasApplied(true);
                }}
            />
            {hasApplied && (
                <Dialog
                    open
                    size="large"
                    fullScreen={isNarrowScreen}
                    onClose={() => setHasApplied(false)}
                    disableCloseOnClickOutside
                >
                    {/* onCloseWorkflow is undefined for technician — the technician's
                       "Continue" button uses useRedirectToPostApplication() internally,
                       bypassing onReturnToProposals entirely. */}
                    <OpenApplicationConfirmationDialog
                        onReturnToProposals={() => onCloseWorkflow?.()}
                    />
                </Dialog>
            )}
        </>
    );
};

export const WellsFargoReviewingWorkflow = () => <WellsFargoReviewingWorkflowInner />;
